import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { AlertModal } from '@/common/components/AlertModal'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import AddStageModal from '../AddStageModal'
import EditStageModal from '../EditStageModal'
import useMutationUpdateBrokeFlexMessageCampaign from '../../api/hooks/useMutationUpdateBrokeFlexMessageCampaign'
import useMutationDeleteBrokeFlexMessageCampaign from '../../api/hooks/useMutationDeleteBrokeFlexMessageCampaign'
import { createBrokeFlexMessageColumns } from './constants.tsx'
import type { BrokeFlexMessageCampaignModel } from '../../types'

interface BrokeFlexMessageTableProps {
	data: BrokeFlexMessageCampaignModel[]
	isLoading?: boolean
}

export default function BrokeFlexMessageTable({
	data,
	isLoading,
}: BrokeFlexMessageTableProps) {
	const navigate = useNavigate()
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [addStageModalOpen, setAddStageModalOpen] = useState(false)
	const [editStageModalOpen, setEditStageModalOpen] = useState(false)
	const [deactivateModalOpen, setDeactivateModalOpen] = useState(false)
	const [selectedCampaign, setSelectedCampaign] =
		useState<BrokeFlexMessageCampaignModel | null>(null)
	const [selectedStage, setSelectedStage] = useState<{
		stage: any
		stageNumber: number
	} | null>(null)
	const [campaignToActivate, setCampaignToActivate] = useState<{
		id: string
		name: string
		activeCampaign: BrokeFlexMessageCampaignModel
	} | null>(null)

	const updateCampaignMutation = useMutationUpdateBrokeFlexMessageCampaign({
		onSuccess: () => {
			toast.success('อัปเดตสถานะสำเร็จ')
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ')
		},
	})

	const deleteCampaignMutation = useMutationDeleteBrokeFlexMessageCampaign({
		onSuccess: () => {
			toast.success('ลบแคมเปญสำเร็จ')
			setDeleteModalOpen(false)
			setSelectedCampaign(null)
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการลบแคมเปญ')
		},
	})

	const handleToggleActive = (id: string, currentStatus: boolean) => {
		const newStatus = !currentStatus

		// If activating, check if there's already an active campaign
		if (newStatus) {
			const activeCampaign = data.find((c) => c.isActive && c.id !== id)
			if (activeCampaign) {
				const campaignToActivateName = data.find((c) => c.id === id)?.name || ''
				// Show modal to confirm deactivation of other campaign
				setCampaignToActivate({
					id,
					name: campaignToActivateName,
					activeCampaign,
				})
				setDeactivateModalOpen(true)
				return
			}
		}

		// Normal toggle
		updateCampaignMutation.mutate({
			id,
			data: {
				isActive: newStatus,
			},
		})
	}

	const handleConfirmDeactivate = () => {
		if (!campaignToActivate) return

		// Deactivate the active campaign first, then activate the new one
		updateCampaignMutation.mutate(
			{
				id: campaignToActivate.activeCampaign.id,
				data: { isActive: false },
			},
			{
				onSuccess: () => {
					// After deactivating the old one, activate the new one
					updateCampaignMutation.mutate({
						id: campaignToActivate.id,
						data: { isActive: true },
					})
					setDeactivateModalOpen(false)
					setCampaignToActivate(null)
				},
			},
		)
	}

	const handleDelete = () => {
		if (!selectedCampaign) return
		deleteCampaignMutation.mutate(selectedCampaign.id)
	}

	const handleDeleteClick = (campaign: BrokeFlexMessageCampaignModel) => {
		setSelectedCampaign(campaign)
		setDeleteModalOpen(true)
	}

	const handleEdit = (id: string) => {
		navigate(`/operator/messaging/broke-flex-message/${id}`)
	}

	const handleAddStage = (campaign: BrokeFlexMessageCampaignModel) => {
		setSelectedCampaign(campaign)
		setAddStageModalOpen(true)
	}

	const handleEditStage = (
		campaign: BrokeFlexMessageCampaignModel,
		stage: any,
		stageNumber: number,
	) => {
		setSelectedCampaign(campaign)
		setSelectedStage({ stage, stageNumber })
		setEditStageModalOpen(true)
	}

	const handleAddStageSuccess = () => {
		setAddStageModalOpen(false)
		setSelectedCampaign(null)
	}

	const handleEditStageSuccess = () => {
		setEditStageModalOpen(false)
		setSelectedCampaign(null)
		setSelectedStage(null)
	}

	const columns = createBrokeFlexMessageColumns({
		onToggleActive: handleToggleActive,
		onDelete: handleDeleteClick,
		onEdit: handleEdit,
		onAddStage: handleAddStage,
		onEditStage: handleEditStage,
	})

	return (
		<>
			<DataTablePaginate
				columns={columns}
				data={data}
				totalItems={data.length}
				isLoading={isLoading}
			/>

			<AlertModal
				isOpen={deleteModalOpen}
				onClose={() => {
					setDeleteModalOpen(false)
					setSelectedCampaign(null)
				}}
				onConfirm={handleDelete}
				loading={deleteCampaignMutation.isPending}
			/>

			<Dialog
				open={deactivateModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setDeactivateModalOpen(false)
						setCampaignToActivate(null)
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>มีแคมเปญที่เปิดใช้งานอยู่แล้ว</DialogTitle>
						<DialogDescription>
							ระบบจะปิดการทำงานของแคมเปญ &quot;
							{campaignToActivate?.activeCampaign.name}&quot; อัตโนมัติ
							เพื่อเปิดใช้งานแคมเปญใหม่ &quot;{campaignToActivate?.name}&quot;
							<br />
							<br />
							<strong className="text-foreground">
								หมายเหตุ: สามารถเปิดใช้งานได้เพียง 1 แคมเปญในเวลาเดียวกัน
							</strong>
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setDeactivateModalOpen(false)
								setCampaignToActivate(null)
							}}
							disabled={updateCampaignMutation.isPending}
						>
							ยกเลิก
						</Button>
						<Button
							onClick={handleConfirmDeactivate}
							disabled={updateCampaignMutation.isPending}
						>
							{updateCampaignMutation.isPending
								? 'กำลังดำเนินการ...'
								: 'ดำเนินการต่อ'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{selectedCampaign && (
				<AddStageModal
					open={addStageModalOpen}
					onClose={() => {
						setAddStageModalOpen(false)
						setSelectedCampaign(null)
					}}
					campaignId={selectedCampaign.id}
					currentStageCount={
						selectedCampaign.brokeNotificationSettings?.stages.length || 0
					}
					onSuccess={handleAddStageSuccess}
				/>
			)}

			{selectedCampaign && selectedStage && (
				<EditStageModal
					open={editStageModalOpen}
					onClose={() => {
						setEditStageModalOpen(false)
						setSelectedCampaign(null)
						setSelectedStage(null)
					}}
					campaignId={selectedCampaign.id}
					stage={selectedStage.stage}
					stageNumber={selectedStage.stageNumber}
					onSuccess={handleEditStageSuccess}
				/>
			)}
		</>
	)
}
